import type {
  AgapellaAccessState,
  AgapellaSubscriptionOffer,
  AgapellaSubscriptionPurchase,
} from '../../types/agapellaSubscription';
import { OPERATOR_NAMES } from '../../legal/operator';
import { isForeignJurisdiction, type JurisdictionSignals } from '../../utils/legalJurisdiction';

/**
 * CORE-1935 — Условия РФ обещают назвать продавца до оплаты. Подписку через
 * СБП продаёт АО; название — из `OPERATOR_NAMES`, ИНН — здесь, пока общей
 * константы реквизитов нет (подвал CORE-1934 держит их в текстах локалей).
 */
export const SELLER_INN = '9729424798';
export const SELLER_LINE = `Продавец — ${OPERATOR_NAMES.ru.ru}, ИНН ${SELLER_INN}`;
export const SELLER_TERMS_LABEL = 'Условия использования';

/**
 * CORE-1562 — правило возврата подписки по ответу юриста 29.10 (ст. 26.1
 * ЗоЗПП, «контентный путь»): до начала оплаченного периода — полный возврат,
 * после — только в исключительных случаях. Дословно то же правило, что в
 * админке у «Вернуть деньги»; своих обещаний сверх него экран не даёт.
 */
export const REFUND_RULE_LINE =
  'Возврат: до начала оплаченного периода — полностью, после начала — только в исключительных случаях';

/**
 * Строка о продавце — только российскому контуру: то же правило, что выбирает
 * редакцию соглашения (CORE-1555). Сомнение решается в пользу показа.
 */
export const showsSellerLine = (signals: JurisdictionSignals): boolean => !isForeignJurisdiction(signals);

/** CORE-1534 — как часто спрашивать исход оплаты, пока она в пути. */
export const PURCHASE_POLL_MS = 4000;

const dayMonth = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

/**
 * Одноразовый код входа из адреса. Приложение кладёт его во фрагмент
 * (`#code=…`), а не в query: фрагмент не уходит на сервер и в логи.
 */
export function readHandoffCode(hash: string | null | undefined): string | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const code = params.get('code')?.trim();
  return code ? code : null;
}

/** Строка статуса под заголовком карточки. */
export function statusLine(state: AgapellaAccessState | null): string {
  if (!state || !state.enabled) return 'Подписка пока недоступна';
  const end = state.endsAtUtc ? dayMonth(state.endsAtUtc) : null;
  switch (state.status) {
    case 'active':
      return end ? `Подписка активна до ${end}` : 'Подписка активна';
    case 'trial_active':
      return end ? `Пробный доступ до ${end}` : 'Пробный доступ';
    case 'trial_ending':
      return end ? `Пробный доступ заканчивается ${end}` : 'Пробный доступ заканчивается';
    case 'trial_expired':
      return 'Пробный доступ закончился';
    case 'expired':
      return end ? `Подписка закончилась ${end}` : 'Подписка закончилась';
    default:
      return 'Подписка не подключена';
  }
}

/** Короткая метка для карточки в «Финансах». */
export function statusBadge(state: AgapellaAccessState | null): string {
  if (!state?.enabled) return 'Не подключена';
  if (state.status === 'active') return 'Активна';
  if (state.status === 'trial_active' || state.status === 'trial_ending') return 'Пробный доступ';
  return 'Не подключена';
}

export function priceLine(offer: AgapellaSubscriptionOffer): string | null {
  if (!offer.priceRub) return null;
  const price = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(offer.priceRub);
  return `${price} ₽ за ${offer.periodDays} дн.`;
}

/** Доступ уже открыт — оплата продлевает его, а не начинается с нуля. */
export function isRenewal(state: AgapellaAccessState | null): boolean {
  return !!state?.hasAccess && state.enabled
    && (state.status === 'active' || state.status === 'trial_active' || state.status === 'trial_ending');
}

export function payButtonLabel(state: AgapellaAccessState | null): string {
  return state?.status === 'active' ? 'Продлить через СБП' : 'Оплатить через СБП';
}

export type PurchaseStage = 'pending' | 'paid' | 'failed';

export function purchaseLine(purchase: AgapellaSubscriptionPurchase): string {
  if (purchase.status === 'paid') {
    return purchase.accessEndsAtUtc
      ? `Оплата прошла. Доступ открыт до ${dayMonth(purchase.accessEndsAtUtc)}`
      : 'Оплата прошла';
  }
  if (purchase.status === 'failed') return 'Оплата не прошла. Можно попробовать ещё раз';
  return 'Ждём подтверждения оплаты — обычно это несколько секунд';
}

/** Номер покупки из параметра маршрута `?purchase=`. */
export function purchaseIdParam(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * CORE-1535 — адрес страницы оплаты для перехода из приложения. Код входа —
 * во фрагменте: он не уходит на сервер и в логи.
 *
 * CORE-1537 — путь `/pay`, а не `/finance/subscription`: тот Android
 * перехватывает (App Links) как возврат с платёжной страницы, и вкладка,
 * открытая приложением на нём же, рисковала бы сразу вернуться в приложение.
 */
export function subscriptionCheckoutUrl(webUrl: string, code: string | null): string {
  const base = `${webUrl.replace(/\/$/, '')}/finance/subscription/pay`;
  return code ? `${base}#code=${encodeURIComponent(code)}` : base;
}

/**
 * CORE-1537 — кнопка «Вернуться в приложение» на сайте после оплаты. Своя
 * схема, а не https: App Link из вкладки срабатывает не всегда (переход без
 * нажатия Chrome оставляет у себя), а `agapella://` по нажатию — всегда.
 */
export function appReturnUrl(purchaseId: number): string {
  return `agapella://finance/subscription?purchase=${purchaseId}`;
}

/**
 * CORE-1537 — чем кончился уход на оплату из приложения:
 * - вернулись по ссылке с платёжной страницы — знаем покупку, её статус и есть ответ;
 * - вернулись переключением (закрыли вкладку) — покупки не знаем, судим по
 *   доступу: продлился — оплачено, нет — не завершено (не оплатил или банк
 *   ещё не подтвердил; различить их отсюда нельзя).
 */
export type CheckoutReturn = 'paid' | 'pending' | 'failed' | 'notPaid';

const endMs = (s: AgapellaAccessState | null): number =>
  s?.endsAtUtc ? Date.parse(s.endsAtUtc) || 0 : 0;

/** Доступ стал оплаченным или оплаченный продлился. */
export function accessExtended(before: AgapellaAccessState | null, after: AgapellaAccessState | null): boolean {
  if (!after?.hasAccess || after.status !== 'active') return false;
  if (before?.status !== 'active') return true;
  return endMs(after) > endMs(before);
}

export function checkoutReturn(
  purchase: AgapellaSubscriptionPurchase | null,
  before: AgapellaAccessState | null,
  after: AgapellaAccessState | null,
): CheckoutReturn {
  if (purchase && purchase.status !== 'pending') return purchase.status;
  if (accessExtended(before, after)) return 'paid';
  return purchase ? 'pending' : 'notPaid';
}

export function checkoutReturnLine(
  outcome: CheckoutReturn,
  purchase: AgapellaSubscriptionPurchase | null,
  after: AgapellaAccessState | null,
): string {
  if (purchase && outcome === purchase.status) return purchaseLine(purchase);
  if (outcome === 'paid') {
    return after?.endsAtUtc ? `Оплата прошла. Доступ открыт до ${dayMonth(after.endsAtUtc)}` : 'Оплата прошла';
  }
  return 'Оплата не завершена. Если она уже прошла, доступ откроется в течение пары минут';
}

/** CORE-1537 — сколько ждать подтверждения после возврата, прежде чем перестать спрашивать. */
export const CHECKOUT_WATCH_MS = 2 * 60 * 1000;

/**
 * CORE-1538 — три момента, когда про пробный доступ говорим сами, окном поверх
 * приложения: оффер после знакомства, напоминание перед концом и «закончился».
 * Каждый — один раз на аккаунт (`seen`), дальше всё то же живёт в «Финансы →
 * Подписка Agapēlla».
 */
export type TrialMoment = 'offer' | 'ending' | 'expired';
export type TrialSeen = Partial<Record<TrialMoment, boolean>>;

export function trialMoment(state: AgapellaAccessState | null, seen: TrialSeen): TrialMoment | null {
  if (!state?.enabled) return null;
  const moment: TrialMoment | null =
    state.status === 'none' && state.trialAvailable ? 'offer'
      : state.status === 'trial_ending' ? 'ending'
        : state.status === 'trial_expired' ? 'expired'
          : null;
  return moment && !seen[moment] ? moment : null;
}

/** Можно ли предложить пробный доступ: подписки нет и trial не использован. */
export function canStartTrial(state: AgapellaAccessState | null): boolean {
  return !!state?.enabled && state.status === 'none' && state.trialAvailable;
}

/** Что известно о туре знакомства в момент, когда оффер решает, показываться ли. */
export interface TourForTrialOffer {
  /** Тур или его прощальная реплика сейчас на экране — по провайдеру, в этой сессии. */
  onScreen: boolean;
  /** Ответ сервера о туре; `null` — ещё не знаем. */
  server: { shouldAutoLaunch: boolean } | null;
}

/**
 * Ждёт ли оффер пробного доступа тура знакомства. v3.4 §6 — trial предлагаем
 * после знакомства, а не вместо него и не поверх него.
 *
 * Ждём только тура, который идёт сейчас (на экране в этой сессии), или того,
 * который вот-вот стартует сам: сервер разрешил автозапуск, а провайдер его ещё
 * не показал (ждёт главную или паузу перед запуском). Пока ответа сервера нет —
 * тоже ждём.
 *
 * Серверный статус сам по себе оффер не держит (CORE-1750). Раньше держал
 * `in_progress`, а брошенный посреди тур так и остаётся `in_progress`: после
 * второго «бросил» сервер его больше не предлагает, и оффер не показался бы
 * никогда. Идущий тур и так виден провайдеру, статус для этого не нужен.
 */
export function trialOfferWaitsForTour({ onScreen, server }: TourForTrialOffer): boolean {
  return onScreen || !server || server.shouldAutoLaunch;
}

export interface TrialMomentCopy {
  title: string;
  lines: string[];
}

/**
 * Тексты окна. `canPay` — правило страны (CORE-1535) и «подписка продаётся»:
 * без него про оплату не говорим вовсе — ни кнопкой, ни словом.
 */
export function trialMomentCopy(
  moment: TrialMoment,
  state: AgapellaAccessState | null,
  canPay: boolean,
): TrialMomentCopy {
  const end = state?.endsAtUtc ? dayMonth(state.endsAtUtc) : null;
  if (moment === 'offer') {
    return {
      title: '7 дней полного доступа',
      lines: [
        'Без оплаты и без автоматического списания',
        'Откроются Agapēlla AI, Live-сессии и материалы, которые входят в Agapēlla. Программы мастеров покупаются отдельно',
        'Через 7 дней доступ закончится сам — отменять ничего не нужно',
      ],
    };
  }
  const freeLayer = 'Аккаунт, календарь, сообщества и библиотека знаний остаются с тобой';
  if (moment === 'ending') {
    return {
      title: end ? `Пробный доступ заканчивается ${end}` : 'Пробный доступ заканчивается',
      lines: [freeLayer, ...(canPay ? ['Подписка сохранит полный доступ — оставшиеся дни не сгорят'] : [])],
    };
  }
  return {
    title: 'Пробный доступ закончился',
    lines: [freeLayer, ...(canPay ? ['Подписка вернёт полный доступ'] : [])],
  };
}

/** Окно после успешного старта trial. */
export function trialStartedLine(state: AgapellaAccessState | null): string {
  return state?.endsAtUtc ? `Полный доступ открыт до ${dayMonth(state.endsAtUtc)}` : 'Полный доступ открыт';
}

/** Почему trial не начался — по коду сервера (CORE-1533). */
export function trialStartErrorLine(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'trial_already_used') return 'Пробный доступ на этом аккаунте уже был';
  return 'Не получилось открыть доступ. Попробуй ещё раз';
}

/**
 * CORE-1951 — контакт для кассового чека. У входа через соцсеть без почты в
 * аккаунте служебный адрес, и чек туда не дойдёт: спрашиваем почту или
 * телефон прямо на странице оплаты.
 */
export const RECEIPT_CONTACT_LABEL = 'Почта или телефон для чека';
export const RECEIPT_CONTACT_HINT = 'Пришлём сюда кассовый чек. В аккаунте нет почты, поэтому спрашиваем здесь';
export const RECEIPT_CONTACT_PLACEHOLDER = 'name@mail.ru или +7 900 123-45-67';
export const RECEIPT_CONTACT_EMPTY = 'Укажи почту или телефон — туда придёт чек';
export const RECEIPT_CONTACT_INVALID = 'Не получается распознать почту или телефон. Проверь, пожалуйста';

/** Строка «куда придёт чек» для аккаунта с настоящей почтой. */
export function receiptEmailLine(email: string | null | undefined): string | null {
  const e = email?.trim();
  return e ? `Чек придёт на ${e}` : null;
}

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Тот же разбор, что на сервере (`NormalizeReceiptContact`): почта — в нижнем
 * регистре, телефон — российский, `+7XXXXXXXXXX`. Не почта и не телефон — null.
 */
export function normalizeReceiptContact(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value || value.length > 254) return null;

  if (value.includes('@')) {
    const email = value.toLowerCase();
    if (!EMAIL_SHAPE.test(email)) return null;
    const domain = email.slice(email.lastIndexOf('@') + 1);
    return domain.endsWith('.noreply') ? null : email;
  }

  if (!/^[\d+\s()-]+$/.test(value) || value.lastIndexOf('+') > 0) return null;
  const digits = value.replace(/\D/g, '');
  const plus = value.startsWith('+');
  let national: string;
  if (digits.length === 11 && digits[0] === '7') national = digits.slice(1);
  else if (digits.length === 11 && digits[0] === '8' && !plus) national = digits.slice(1);
  else if (digits.length === 10 && !plus && digits[0] !== '8') national = digits;
  else return null;
  return '3489'.includes(national[0]) ? `+7${national}` : null;
}

/** Что сказать под полем контакта, если сервер не начал оплату из-за него. */
export function receiptContactErrorLine(error: unknown): string | null {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'RECEIPT_CONTACT_REQUIRED') return RECEIPT_CONTACT_EMPTY;
  if (code === 'RECEIPT_CONTACT_INVALID') return RECEIPT_CONTACT_INVALID;
  return null;
}

/**
 * CORE-1538 — бейдж доступа на карточке (v3.4 §5): «Бесплатно», «Входит в
 * Agapēlla», «Купить отдельно + цена».
 *
 * `canPay === false` (Android вне РФ, iOS) — слов о покупке нет, остаётся
 * только цена, как было до бейджей.
 */
export type AccessBadgeKind = 'free' | 'included' | 'separate' | 'price';

export interface AccessBadgeModel {
  kind: AccessBadgeKind;
  label: string;
}

export function accessBadge(
  item: { price: number | null; priceLabel?: string | null; includedInAgapella?: boolean | null },
  canPay: boolean | null,
): AccessBadgeModel | null {
  if (item.includedInAgapella) return { kind: 'included', label: 'Входит в Agapēlla' };
  // Цена не задана (черновик) — печатаем то, что карточка сказала бы и так.
  if (item.price == null) return item.priceLabel ? { kind: 'price', label: item.priceLabel } : null;
  if (item.price === 0) return { kind: 'free', label: 'Бесплатно' };
  const price = item.priceLabel ?? String(item.price);
  if (canPay !== true) return { kind: 'price', label: price };
  return { kind: 'separate', label: `Купить отдельно · ${price}` };
}

/**
 * CORE-2075 — оплата подписки баллами (флаг `points_payment`). Сколько баллов
 * можно списать, считает сервер (`pointsApplicableMinor` в оффере: только
 * баллы, прошедшие 14 дней ожидания, и ноль при долге после возврата). Клиент
 * только переводит копейки в целые баллы (1 балл = 1 ₽) и вычитает их из цены.
 */
export interface PointsPaymentOption {
  /** Сколько баллов спишется. */
  points: number;
  /** Остаток деньгами, ₽: цена минус баллы, не меньше нуля. */
  moneyRub: number;
  /** Баллы покрывают всю цену — оплаты у провайдера не будет. */
  coversAll: boolean;
}

/**
 * Показывать ли «Оплатить баллами» и с какими цифрами. `null` — флаг выключен
 * (на клиенте или на сервере), подписка не продаётся или тратить нечего:
 * экран тогда такой же, как без баллов.
 */
export function pointsPaymentOption(
  offer: AgapellaSubscriptionOffer | null,
  flagEnabled: boolean,
): PointsPaymentOption | null {
  if (!flagEnabled || !offer?.forSale || !offer.pointsPaymentEnabled) return null;
  const price = offer.priceRub ?? 0;
  if (!(price > 0)) return null;
  const priceMinor = Math.round(price * 100);
  const applicableMinor = Math.min(Math.max(0, offer.pointsApplicableMinor ?? 0), priceMinor);
  const points = Math.floor(applicableMinor / 100);
  if (points < 1) return null;
  const moneyMinor = Math.max(0, priceMinor - points * 100);
  return { points, moneyRub: moneyMinor / 100, coversAll: moneyMinor === 0 };
}

/** Тело `POST /api/agapella-subscription/purchase`. Без баллов и контакта — без тела, как раньше. */
export function purchaseRequestBody(
  receiptContact: string | null,
  usePoints: boolean,
): { receiptContact?: string; usePoints?: true } | undefined {
  if (!receiptContact && !usePoints) return undefined;
  return {
    ...(receiptContact ? { receiptContact } : {}),
    ...(usePoints ? { usePoints: true as const } : {}),
  };
}

/** «1 балл», «2 балла», «5 баллов». */
export function pointsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'балл';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'балла';
  return 'баллов';
}

const rub = (value: number): string =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);

/** «1 000 баллов» — с неразрывным пробелом в разрядах. */
export function pointsAmount(points: number): string {
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(points)} ${pointsWord(points)}`;
}

export const POINTS_TOGGLE_LABEL = 'Оплатить баллами';
export const POINTS_FULL_BUTTON_LABEL = 'Оплатить баллами полностью';

/** Строка под переключателем: что спишется и сколько останется заплатить. */
export function pointsPaymentLine(option: PointsPaymentOption): string {
  if (option.coversAll) {
    return `Баллов хватает на всю подписку: спишем ${pointsAmount(option.points)}, платить через СБП не нужно`;
  }
  return `Спишем ${pointsAmount(option.points)}, к оплате через СБП — ${rub(option.moneyRub)} ₽`;
}

/** Подпись кнопки оплаты с учётом баллов. */
export function payButtonLabelWithPoints(
  state: AgapellaAccessState | null,
  option: PointsPaymentOption | null,
  usePoints: boolean,
): string {
  if (usePoints && option?.coversAll) return POINTS_FULL_BUTTON_LABEL;
  return payButtonLabel(state);
}

/**
 * Сервер списал меньше баллов, чем обещал экран (часть баллов ушла на другую
 * покупку или снова ждёт после возврата). Тогда не уводим человека платить
 * бо́льшую сумму молча, а показываем новый расчёт.
 */
export function pointsShortfall(purchase: AgapellaSubscriptionPurchase, option: PointsPaymentOption | null): boolean {
  if (!option) return false;
  return (purchase.pointsAppliedMinor ?? 0) < option.points * 100;
}

export function pointsShortfallLine(purchase: AgapellaSubscriptionPurchase): string {
  const applied = Math.floor((purchase.pointsAppliedMinor ?? 0) / 100);
  const money = purchase.amountToPay ?? purchase.amount;
  if (applied < 1) {
    return `Баллы сейчас недоступны — они могут ещё ждать зачисления. К оплате через СБП — ${rub(money)} ₽. Если всё верно, нажми ещё раз`;
  }
  return `Сейчас доступно меньше баллов: спишем ${pointsAmount(applied)}, к оплате через СБП — ${rub(money)} ₽. Если всё верно, нажми ещё раз`;
}

export const POINTS_START_ERROR = 'Не получилось списать баллы. Попробуй ещё раз или выключи оплату баллами';

/** Итог покупки, оплаченной баллами целиком. */
export function pointsPaidLine(purchase: AgapellaSubscriptionPurchase): string | null {
  if (purchase.status !== 'paid' || !(purchase.pointsAppliedMinor ?? 0) || (purchase.amountToPay ?? 1) > 0) return null;
  return purchase.accessEndsAtUtc
    ? `Оплачено баллами. Доступ открыт до ${dayMonth(purchase.accessEndsAtUtc)}`
    : 'Оплачено баллами';
}
